import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { FileBox, Lock, User, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

const schema = z.object({
  email: z.string().trim().email("Email inválido").max(255),
  password: z.string().min(1, "Informe a senha").max(72),
});

export default function MinimalLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Força Light Mode nesta tela se o usuário quiser, mas o AppShell lida com isso.
  // Vamos manter neutro para aceitar dark mode se o sistema estiver assim.
  
  useEffect(() => {
    // Check for session conflict error
    const params = new URLSearchParams(location.search);
    if (params.get("error") === "session_conflict") {
      toast({
        title: "Sessão encerrada",
        description: "Sua conta foi acessada em outro dispositivo. O acesso nesta máquina foi desconectado.",
        variant: "destructive",
      });
    }
  }, [location.search]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse({ email, password });
    if (!parsed.success) {
      toast({ title: "Verifique os campos", description: parsed.error.issues[0].message, variant: "destructive" });
      return;
    }
    setLoading(true);
    
    // Timeout de segurança para o botão de login (10s)
    const loginTimeout = setTimeout(() => {
      if (loading) {
        setLoading(false);
        toast({ title: "Tempo esgotado", description: "O servidor demorou muito a responder. Verifique sua conexão.", variant: "destructive" });
      }
    }, 10000);

    try {
      const { error } = await supabase.auth.signInWithPassword(parsed.data);
      if (error) {
        console.error("[Login] Supabase error:", error);
        // Mapeia os erros conhecidos do Supabase para mensagens em português
        let msg = error.message;
        if (error.message?.includes("Invalid login credentials")) msg = "Email ou senha incorretos.";
        else if (error.message?.includes("Email not confirmed")) msg = "Email ainda não confirmado. Verifique sua caixa de entrada.";
        else if (error.message?.includes("Too many requests")) msg = "Muitas tentativas. Aguarde alguns minutos.";
        else if (error.message?.includes("Invalid API key") || error.message?.includes("No API key")) msg = "Erro de configuração do servidor (API Key inválida). Contate o suporte.";
        else if (error.message?.includes("fetch") || error.message?.includes("network") || error.message?.includes("Failed to fetch")) msg = "Erro de conexão com o servidor. Verifique sua internet.";
        toast({ title: "Falha no login", description: msg, variant: "destructive" });
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      console.error("[Login] Unexpected error:", err);
      toast({ title: "Erro inesperado", description: "Ocorreu um erro ao tentar entrar. Tente novamente.", variant: "destructive" });
    } finally {
      clearTimeout(loginTimeout);
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background text-foreground font-sans p-4 relative overflow-hidden">
      
      {/* Dynamic Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[10%] -left-[10%] w-[60%] h-[60%] bg-primary/5 liquid-blob blur-[100px] opacity-60" />
        <div className="absolute -bottom-[10%] -right-[10%] w-[50%] h-[50%] bg-primary/10 liquid-blob blur-[120px] opacity-40" style={{ animationDelay: "-5s" }} />
      </div>

      <div className="relative w-full max-w-[360px] z-10 animate-in fade-in zoom-in-95 duration-700">
        <div className="absolute -inset-4 bg-primary/5 rounded-[3rem] blur-3xl opacity-50" />
        
        <div className="glass-card p-8 relative border-white/20 dark:border-white/5">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="relative inline-block mb-4">
              <div className="absolute -inset-2 bg-primary/20 rounded-full blur-xl animate-pulse" />
              <div className="size-14 rounded-2xl bg-primary flex items-center justify-center shadow-xl shadow-primary/40 relative z-10 rotate-3 hover:rotate-0 transition-transform duration-500">
                <FileBox className="size-7 text-white" />
              </div>
            </div>
            <h1 className="text-2xl font-black tracking-tighter text-foreground mb-1 italic">DataGrid <span className="text-primary not-italic">XML</span></h1>
            <p className="text-[9px] text-muted-foreground uppercase tracking-[0.2em] font-black opacity-60">Portal de Acesso</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1 group">
              <Label htmlFor="email" className="text-[9px] font-black uppercase tracking-widest text-muted-foreground ml-1 group-focus-within:text-primary transition-colors">E-mail</Label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground/50 group-focus-within:text-primary transition-colors">
                  <User className="size-3.5" />
                </div>
                <Input 
                  id="email" 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                  className="input-premium h-11 pl-11 bg-white/5"
                  placeholder="Seu e-mail"
                />
              </div>
            </div>

            <div className="space-y-1 group">
              <div className="flex items-center justify-between ml-1">
                <Label htmlFor="password" className="text-[9px] font-black uppercase tracking-widest text-muted-foreground group-focus-within:text-primary transition-colors">Senha</Label>
                <Link to="/forgot-password" className="text-[9px] font-bold text-primary/60 hover:text-primary transition-colors uppercase tracking-wider pointer-events-none opacity-50">Esqueceu?</Link>
              </div>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground/50 group-focus-within:text-primary transition-colors">
                  <Lock className="size-3.5" />
                </div>
                <Input 
                  id="password" 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                  className="input-premium h-11 pl-11 bg-white/5"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full h-11 text-[10px] font-black tracking-[0.2em]" 
              disabled={loading}
            >
              {loading ? "PROCESSANDO..." : "ENTRAR NO SISTEMA"}
            </Button>
          </form>

          <div className="mt-6 text-center border-t border-foreground/5 pt-6">
            <p className="text-[11px] text-muted-foreground font-medium uppercase tracking-widest">
              Novo por aqui? <Link to="/signup" className="text-primary font-black hover:scale-105 transition-transform inline-block ml-1">Criar Conta</Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-[9px] text-muted-foreground font-bold uppercase tracking-[0.2em] opacity-40">
            Powered by DataGrid XML Engine • 2024
          </p>
        </div>
      </div>
    </div>
  );
}
